import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, PieChart, Receipt, Calculator } from 'lucide-react';
import type { DashboardMetrics, ArgentinaMetrics, CountryCode } from '../utils/calculations';
import { formatCurrency } from '../utils/calculations';
import { Tooltip } from './Tooltip';
interface MainMetricsProps {
  metrics: DashboardMetrics;
  arMetrics?: ArgentinaMetrics;
  country: CountryCode;
}
export function MainMetrics({
  metrics,
  arMetrics,
  country
}: MainMetricsProps) {
  const isAr = country === 'AR' && arMetrics;
  if (isAr) {
    const profitPositive = arMetrics.utilidadReal >= 0;
    return <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.2
    }} className="flex flex-col gap-4 h-full" data-id="element-168">
        {/* Total Comisiones + Margen Bruto */}
        <div className="glass-card p-5 group hover:border-cyber-green/30 transition-colors" data-id="element-169">
          <div className="grid grid-cols-2 gap-4" data-id="element-170">
            <div data-id="element-171">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-172">
                <Receipt className="w-3.5 h-3.5 text-yellow-400" data-id="element-173" />
                <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-174">
                  Total Comisiones
                </h3>
              </div>
              <div className="text-2xl font-black text-yellow-400 tracking-tight" data-id="element-175">
                {formatCurrency(arMetrics.totalComisiones, country)}
              </div>
              <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-176">
                IIBB + MP + Cuotas + Tienda
              </p>
            </div>
            <div data-id="element-177">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-178">
                <Calculator className="w-3.5 h-3.5 text-cyber-green" data-id="element-179" />
                <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-180">
                  Margen Bruto
                </h3>
                <Tooltip text="Precio menos producto, envío y comisiones (antes de publicidad)." data-id="element-181" />
              </div>
              <div className="text-2xl font-black text-cyber-green tracking-tight" data-id="element-182">
                {formatCurrency(arMetrics.margenBruto, country)}
              </div>
              <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-183">
                Disponible para Meta Ads
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-cyber-green/10" data-id="element-184">
            <div className="flex items-center gap-2 mb-1" data-id="element-185">
              <TrendingUp className="w-3.5 h-3.5 text-cyber-green/60" data-id="element-186" />
              <h4 className="text-xs font-bold tracking-widest text-cyber-green/60 uppercase" data-id="element-187">
                ROAS Break-Even
              </h4>
            </div>
            <div className="text-3xl font-black text-cyber-green tracking-tight" data-id="element-188">
              {arMetrics.roasBreakEven.toFixed(2)}
            </div>
            <p className="text-[10px] text-cyber-green/40 font-medium mt-1" data-id="element-189">
              ROAS mínimo para no perder en Meta.
            </p>
          </div>
        </div>

        {/* Utilidad Real */}
        <div className={`glass-card p-6 flex-grow flex flex-col justify-center relative overflow-hidden ${profitPositive ? 'border-salmon/30 shadow-glow-salmon' : 'border-danger-red/30 shadow-glow-red'}`} data-id="element-190">
          <div className={`absolute top-0 right-0 w-32 h-32 ${profitPositive ? 'bg-salmon/10' : 'bg-danger-red/10'} rounded-full blur-3xl -mr-10 -mt-10`} data-id="element-191"></div>
          <div className="relative z-10" data-id="element-192">
            <div className="flex items-center gap-2 mb-2" data-id="element-193">
              <div className={`w-2 h-2 rounded-full ${profitPositive ? 'bg-salmon' : 'bg-danger-red'} animate-pulse`} data-id="element-194"></div>
              <h3 className={`text-sm font-bold tracking-widest uppercase ${profitPositive ? 'text-salmon' : 'text-danger-red'}`} data-id="element-195">
                Utilidad Real
              </h3>
              <Tooltip text="Margen Bruto menos tu CPA actual en Meta." data-id="element-196" />
            </div>
            <div className={`text-5xl font-black tracking-tight mb-1 ${profitPositive ? 'text-white' : 'text-danger-red'}`} data-id="element-197">
              {arMetrics.utilidadReal < 0 ? '-' : ''}
              {formatCurrency(arMetrics.utilidadReal, country)}
            </div>
            <p className="text-xs text-white/50 font-medium" data-id="element-198">
              {profitPositive ? 'Ganancia neta por venta' : '⚠ Pérdida — tu CPA es demasiado alto'}
            </p>
          </div>
        </div>

        {/* Margen Real */}
        <div className="glass-card p-5 flex items-center justify-between" data-id="element-199">
          <div data-id="element-200">
            <div className="flex items-center gap-2 mb-1" data-id="element-201">
              <PieChart className="w-4 h-4 text-white/70" data-id="element-202" />
              <h3 className="text-xs font-bold tracking-widest text-white/60 uppercase" data-id="element-203">
                Margen Neto Real
              </h3>
            </div>
            <div className={`text-2xl font-bold tracking-tight ${arMetrics.margenReal >= 0 ? 'text-white' : 'text-danger-red'}`} data-id="element-204">
              {arMetrics.margenReal.toFixed(2)}%
            </div>
          </div>
        </div>
      </motion.div>;
  }
  // Colombia / Mexico (COD)
  const profitPositive = metrics.netProfit >= 0;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className="flex flex-col gap-4 h-full" data-id="element-205">
      <div className="glass-card p-5 group hover:border-cyber-green/30 transition-colors" data-id="element-206">
        <div className="grid grid-cols-2 gap-4" data-id="element-207">
          <div data-id="element-208">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-209">
              <Target className="w-3.5 h-3.5 text-cyber-green" data-id="element-210" />
              <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-211">
                CPA Meta
              </h3>
            </div>
            <div className="text-2xl font-black text-cyber-green tracking-tight" data-id="element-212">
              {formatCurrency(metrics.cpaIdeal, country)}
            </div>
            <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-213">
              Le pagas a Meta
            </p>
          </div>
          <div data-id="element-214">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-215">
              <Target className="w-3.5 h-3.5 text-salmon" data-id="element-216" />
              <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-217">
                CPA Costeado
              </h3>
              <Tooltip text="Costo real de publicidad por cada entrega exitosa, considerando las devoluciones." data-id="element-218" />
            </div>
            <div className="text-2xl font-black text-salmon tracking-tight" data-id="element-219">
              {formatCurrency(metrics.cpaCosteado, country)}
            </div>
            <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-220">
              Te cuesta por entrega
            </p>
          </div>
        </div>
        <p className="text-[10px] text-white/20 font-medium mt-3 pt-3 border-t border-white/5 text-center" data-id="element-221">
          Lo que le pagas a Meta vs. lo que te cuesta cada entrega real
        </p>
        <div className="mt-3 pt-3 border-t border-cyber-green/10" data-id="element-222">
          <div className="flex items-center gap-2 mb-1" data-id="element-223">
            <TrendingUp className="w-3.5 h-3.5 text-cyber-green/60" data-id="element-224" />
            <h4 className="text-xs font-bold tracking-widest text-cyber-green/60 uppercase" data-id="element-225">
              ROAS
            </h4>
            <Tooltip text="Este es el ROAS que debes ver en Meta para alcanzar tu utilidad neta objetivo." data-id="element-226" />
          </div>
          <div className="text-3xl font-black text-cyber-green tracking-tight" data-id="element-227">
            {metrics.roasTarget.toFixed(2)}
          </div>
          <p className="text-[10px] text-cyber-green/40 font-medium mt-1" data-id="element-228">
            El ROAS que debes ver en Meta para cumplir tu utilidad.
          </p>
        </div>
      </div>

      <div className={`glass-card p-6 flex-grow flex flex-col justify-center relative overflow-hidden ${profitPositive ? 'border-salmon/30 shadow-glow-salmon' : 'border-danger-red/30 shadow-glow-red'}`} data-id="element-229">
        <div className={`absolute top-0 right-0 w-32 h-32 ${profitPositive ? 'bg-salmon/10' : 'bg-danger-red/10'} rounded-full blur-3xl -mr-10 -mt-10`} data-id="element-230"></div>
        <div className="relative z-10" data-id="element-231">
          <div className="flex items-center gap-2 mb-2" data-id="element-232">
            <div className={`w-2 h-2 rounded-full ${profitPositive ? 'bg-salmon' : 'bg-danger-red'} animate-pulse`} data-id="element-233"></div>
            <h3 className={`text-sm font-bold tracking-widest uppercase ${profitPositive ? 'text-salmon' : 'text-danger-red'}`} data-id="element-234">
              Utilidad Neta
            </h3>
            <Tooltip text="Ganancia real después de pagar publicidad, producto y logística (Fletes ida y vuelta)." data-id="element-235" />
          </div>
          <div className={`text-5xl font-black tracking-tight mb-1 ${profitPositive ? 'text-white' : 'text-danger-red'}`} data-id="element-236">
            {metrics.netProfit < 0 ? '-' : ''}
            {formatCurrency(metrics.netProfit, country)}
          </div>
          <p className="text-xs text-white/50 font-medium" data-id="element-237">
            {profitPositive ? 'Ganancia por entrega exitosa' : '⚠ Pérdida por entrega — ajusta tus costos'}
          </p>
        </div>
      </div>

      <div className="glass-card p-5 flex items-center justify-between" data-id="element-238">
        <div data-id="element-239">
          <div className="flex items-center gap-2 mb-1" data-id="element-240">
            <PieChart className="w-4 h-4 text-white/70" data-id="element-241" />
            <h3 className="text-xs font-bold tracking-widest text-white/60 uppercase" data-id="element-242">
              Margen Neto
            </h3>
          </div>
          <div className={`text-2xl font-bold tracking-tight ${metrics.netMargin >= 0 ? 'text-white' : 'text-danger-red'}`} data-id="element-243">
            {metrics.netMargin.toFixed(2)}%
          </div>
        </div>
      </div>
    </motion.div>;
}